// Staff activity log (admin and office): what has been done on the platform, across every kind of record, newest
// first. Read only: it lists the platform's own AuditLog through GET /api/audit-log/feed and stores nothing. Filter
// by person, record type and date range; each row opens its record where that record has a page. The before and
// after details are not shown here (a record's own page has its timeline), so this list is safe to scan.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { ActivityLogFilters, ActivityLogPage as ActivityPage } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { formatDateTime } from "../../lib/format";

const PAGE_SIZE = 25;

// Plain English for a raw key (an action or a record type): underscores and camelCase become words.
export function humaniseKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return words.length > 0 ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

const controlCls = "min-h-11 w-full rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm text-ele-strong";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-widest text-ele-body opacity-70";

export function ActivityLogPage() {
  const [filters, setFilters] = useState<ActivityLogFilters | null>(null);
  const [userId, setUserId] = useState("");
  const [entity, setEntity] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ActivityPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.auditLog
      .feedFilters()
      .then((f) => live && setFilters(f))
      .catch(() => live && setFilters({ users: [], entities: [] })); // the list still works without the pick lists
    return () => {
      live = false;
    };
  }, []);

  const load = useCallback(() => {
    if (from !== "" && to !== "" && from > to) return undefined; // the alert below says why; nothing is asked of the server
    let live = true;
    setError(null);
    api.auditLog
      .feed({ userId, entity, from, to, page, pageSize: PAGE_SIZE })
      .then((d) => live && setData(d))
      .catch(() => {
        if (!live) return;
        setData(null);
        setError("The activity log could not be loaded. Try again in a moment.");
      });
    return () => {
      live = false;
    };
  }, [userId, entity, from, to, page]);

  useEffect(() => load(), [load]);

  const dateOrderBad = from !== "" && to !== "" && from > to;
  const changeFilter = (set: (v: string) => void) => (e: { target: { value: string } }) => {
    set(e.target.value);
    setPage(1);
  };
  const anyFilter = userId !== "" || entity !== "" || from !== "" || to !== "";

  return (
    <div className="space-y-4">
      <PageHeader title="Staff activity" subtitle="Who did what on the platform, newest first. This is a read only list of the platform's own record of changes." />

      <form className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" onSubmit={(e) => e.preventDefault()} aria-label="Filter the activity log">
        <label>
          <span className={labelCls}>Person</span>
          <select aria-label="Person" className={controlCls} value={userId} onChange={changeFilter(setUserId)}>
            <option value="">Everyone</option>
            <option value="system">System (automatic)</option>
            {(filters?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Record type</span>
          <select aria-label="Record type" className={controlCls} value={entity} onChange={changeFilter(setEntity)}>
            <option value="">All records</option>
            {(filters?.entities ?? []).map((e) => (
              <option key={e} value={e}>
                {humaniseKey(e)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>From</span>
          <input aria-label="From date" type="date" className={controlCls} value={from} max={to || undefined} onChange={changeFilter(setFrom)} />
        </label>
        <label>
          <span className={labelCls}>To</span>
          <input aria-label="To date" type="date" className={controlCls} value={to} min={from || undefined} onChange={changeFilter(setTo)} />
        </label>
      </form>
      {anyFilter && (
        <button
          type="button"
          className="min-h-11 rounded-ele border border-ele-hairline px-3 text-sm text-ele-body hover:text-ele-strong"
          onClick={() => {
            setUserId("");
            setEntity("");
            setFrom("");
            setTo("");
            setPage(1);
          }}
        >
          Clear filters
        </button>
      )}
      {dateOrderBad && (
        <p role="alert" className="text-sm text-ele-danger">
          The end day is before the start day.
        </p>
      )}

      {error ? (
        <p role="alert" className="text-sm text-ele-danger">
          {error}
        </p>
      ) : data === null ? (
        <Loader label="Loading the activity log..." />
      ) : data.items.length === 0 ? (
        <EmptyState message={anyFilter ? "No activity matches these filters." : "No activity has been recorded yet."} role={anyFilter ? "status" : undefined} />
      ) : (
        <>
          <p className="text-sm text-ele-body" aria-live="polite">
            {data.total} {data.total === 1 ? "entry" : "entries"}
          </p>
          <ul className="space-y-2" aria-label="Activity">
            {data.items.map((it) => (
              <li key={it.id} className="rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span className="min-w-0 break-words text-ele-strong">{humaniseKey(it.action)}</span>
                  <time dateTime={it.at} className="text-xs text-ele-body opacity-70">
                    {formatDateTime(it.at)}
                  </time>
                </div>
                <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-ele-body">
                  <span className="min-w-0 break-words">
                    {it.actorName} | {humaniseKey(it.entity)}
                  </span>
                  {it.recordPath ? (
                    <Link to={it.recordPath} className="inline-flex min-h-11 items-center text-ele-blue underline-offset-2 hover:underline">
                      Open record
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          <nav className="flex items-center justify-between gap-3" aria-label="Pages">
            <button
              type="button"
              className="min-h-11 rounded-ele border border-ele-hairline px-4 text-sm disabled:opacity-40"
              disabled={data.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="text-sm text-ele-body">
              Page {data.page} of {data.pageCount}
            </span>
            <button
              type="button"
              className="min-h-11 rounded-ele border border-ele-hairline px-4 text-sm disabled:opacity-40"
              disabled={data.page >= data.pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </nav>
        </>
      )}
    </div>
  );
}
